/**
 * Alpine.js Name + Username Editor Component
 * Combined editor: inline on desktop, single bottom sheet on mobile
 */

import { editorBase } from './alpine-editor-base.js';
import { SM_BREAKPOINT } from './breakpoints.js';
import { formatDurationShort } from './time-utils.js';

export function nameUsernameEditor() {
    return {
        // State
        name: '',
        originalName: '',
        username: '',
        originalUsername: '',
        editingName: false,
        editingUsername: false,
        sheetOpen: false,
        isSaving: false,
        error: null,
        accountEndpoint: '/v1/account/self',

        // Cooldown
        usernameCooldownLastmod: '',
        usernameCooldownInterval: 0,
        cooldownAvailableInText: '',

        get cooldownBlocked() {
            if (!this.usernameCooldownLastmod || !this.usernameCooldownInterval) return false;
            const lastmodTime = new Date(this.usernameCooldownLastmod.replace(' ', 'T') + 'Z').getTime();
            const elapsed = (Date.now() - lastmodTime) / 1000;
            return elapsed < this.usernameCooldownInterval;
        },

        get cooldownMessage() {
            if (!this.cooldownBlocked) return '';
            const lastmodTime = new Date(this.usernameCooldownLastmod.replace(' ', 'T') + 'Z').getTime();
            const elapsed = (Date.now() - lastmodTime) / 1000;
            const remaining = this.usernameCooldownInterval - elapsed;
            const timeStr = formatDurationShort(remaining);
            return this.cooldownAvailableInText
                ? this.cooldownAvailableInText.replace('%time%', timeStr)
                : `Available again in ${timeStr}`;
        },

        get hasNameChanges() {
            return this.name !== this.originalName;
        },

        get hasUsernameChanges() {
            return this.username !== this.originalUsername;
        },

        init() {
            const el = this.$el;
            this.name = el.dataset.name || '';
            this.originalName = this.name;
            this.username = el.dataset.username || '';
            this.originalUsername = this.username;
            this.usernameCooldownLastmod = el.dataset.usernameLastmod || '';
            this.usernameCooldownInterval = parseInt(el.dataset.usernameCooldown, 10) || 0;
            this.cooldownAvailableInText = el.dataset.cooldownAvailableIn || '';
            this.accountEndpoint = el.dataset.accountEndpoint || this.accountEndpoint;
        },

        // Desktop inline editing
        startEditName() {
            this.editingName = true;
            this.error = null;
            this.$nextTick(() => {
                const input = this.$refs.nameInput;
                if (input) { input.focus(); input.select(); }
            });
        },

        startEditUsername() {
            this.editingUsername = true;
            this.error = null;
            this.$nextTick(() => {
                const input = this.$refs.usernameInput;
                if (input) { input.focus(); input.select(); }
            });
        },

        cancelName() {
            this.name = this.originalName;
            this.editingName = false;
            this.error = null;
        },

        cancelUsername() {
            this.username = this.originalUsername;
            this.editingUsername = false;
            this.error = null;
        },

        async saveName() {
            if (!this.hasNameChanges || this.isSaving) return;
            this.isSaving = true;
            this.error = null;
            try {
                await editorBase.saveField(this.accountEndpoint, 'name', this.name);
                this.originalName = this.name;
                this.editingName = false;
            } catch (e) {
                console.error('Save name error:', e);
                this.error = e.message || 'Failed to save';
            } finally {
                this.isSaving = false;
            }
        },

        async saveUsername() {
            if (!this.hasUsernameChanges || this.isSaving) return;
            this.isSaving = true;
            this.error = null;
            try {
                await editorBase.saveField(this.accountEndpoint, 'username', this.username);
                this.originalUsername = this.username;
                this.usernameCooldownLastmod = new Date().toISOString().replace('T', ' ').substring(0, 19);
                this.editingUsername = false;
            } catch (e) {
                console.error('Save username error:', e);
                this.error = e.message || 'Failed to save';
            } finally {
                this.isSaving = false;
            }
        },

        // Mobile bottom sheet
        openSheet() {
            this.sheetOpen = true;
            this.error = null;
            window.scrollLock?.lock();
            this.$nextTick(() => {
                window.LucideIcons?.initialize(this.$el);
                this.$refs.nameInputMobile?.focus();
            });
        },

        closeSheet() {
            this.name = this.originalName;
            this.username = this.originalUsername;
            this.sheetOpen = false;
            this.error = null;
            window.scrollLock?.unlock();
        },

        async saveSheet() {
            this.isSaving = true;
            this.error = null;
            try {
                // Save sequentially — name first, then username
                if (this.hasNameChanges) {
                    await editorBase.saveField(this.accountEndpoint, 'name', this.name);
                    this.originalName = this.name;
                }
                if (this.hasUsernameChanges && !this.cooldownBlocked) {
                    await editorBase.saveField(this.accountEndpoint, 'username', this.username);
                    this.originalUsername = this.username;
                    this.usernameCooldownLastmod = new Date().toISOString().replace('T', ' ').substring(0, 19);
                }
                this.sheetOpen = false;
                window.scrollLock?.unlock();
            } catch (e) {
                console.error('Save sheet error:', e);
                this.error = e.message || 'Failed to save';
            } finally {
                this.isSaving = false;
            }
        }
    };
}
